/**
 * Target languages that carry the Atlas course track.
 * German and Swiss German stay on their existing sections.
 * English is included here as a course route only.
 */

export const COURSE_TARGET_LANGS = Object.freeze([
  "bg",
  "es",
  "cs",
  "da",
  "de",
  "et",
  "el",
  "en",
  "fr",
  "ga",
  "hr",
  "it",
  "lv",
  "lt",
  "hu",
  "mt",
  "nl",
  "pl",
  "pt",
  "ro",
  "sk",
  "sl",
  "fi",
  "sv",
  "hi",
  "hy",
  "th",
]);

/** English names. Course chrome stays English until the structure settles. */
export const COURSE_LANGUAGE_NAMES = Object.freeze({
  bg: "Bulgarian",
  es: "Spanish",
  cs: "Czech",
  da: "Danish",
  de: "German",
  et: "Estonian",
  el: "Greek",
  en: "English",
  fr: "French",
  ga: "Irish",
  hr: "Croatian",
  it: "Italian",
  lv: "Latvian",
  lt: "Lithuanian",
  hu: "Hungarian",
  mt: "Maltese",
  nl: "Dutch",
  pl: "Polish",
  pt: "Portuguese",
  ro: "Romanian",
  sk: "Slovak",
  sl: "Slovenian",
  fi: "Finnish",
  sv: "Swedish",
  hi: "Hindi",
  hy: "Eastern Armenian",
  th: "Thai",
});

/** Same ASCII slug in every interface language. Existing targets keep their own slugs. */
export const COURSE_TARGET_SLUGS = Object.freeze({
  bg: "bulgarian",
  es: "spanish",
  cs: "czech",
  da: "danish",
  et: "estonian",
  el: "greek",
  fr: "french",
  ga: "irish",
  hr: "croatian",
  it: "italian",
  lv: "latvian",
  lt: "lithuanian",
  hu: "hungarian",
  mt: "maltese",
  nl: "dutch",
  pl: "polish",
  pt: "portuguese",
  ro: "romanian",
  sk: "slovak",
  sl: "slovenian",
  fi: "finnish",
  sv: "swedish",
  hi: "hindi",
  hy: "armenian",
  th: "thai",
});

export const COURSE_TARGET_LANDING = Object.freeze(
  Object.fromEntries(Object.entries(COURSE_TARGET_SLUGS).map(([code, slug]) => [code, `learn-${slug}`])),
);

const COURSE_TARGET_SET = new Set(COURSE_TARGET_LANGS);
const PUBLIC_TARGET_LANGS = Object.freeze(["de", "ch", "es"]);
const PUBLIC_COURSE_TARGETS = new Set(["es"]);

export function isCourseTarget(code) {
  return COURSE_TARGET_SET.has(String(code || "").trim().toLowerCase());
}

export function courseLanguageName(code) {
  const c = String(code || "").trim().toLowerCase();
  return COURSE_LANGUAGE_NAMES[c] || "";
}

/** Production default is off, so new language links stay hidden unless this is set. */
export function showNewTargetLangs() {
  const value = String(process.env.LL_SHOW_NEW_TARGET_LANGS || "").trim().toLowerCase();
  return value === "1" || value === "true" || value === "yes";
}

/** Languages offered on the home page and in the learn menu. */
export function getNavTargetLangs() {
  if (!showNewTargetLangs()) return PUBLIC_TARGET_LANGS;
  const seen = new Set(PUBLIC_TARGET_LANGS);
  const out = [...PUBLIC_TARGET_LANGS];
  for (const code of COURSE_TARGET_LANGS) {
    if (seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
}

export function coursePagesAreHidden(targetCode) {
  const code = String(targetCode || "").trim().toLowerCase();
  if (PUBLIC_TARGET_LANGS.includes(code)) return false;
  return isCourseTarget(code) && !showNewTargetLangs();
}

/** Course pages that stay off menus and out of the sitemap. German keeps its public site; only its course section is unlisted. */
export function courseSectionIsUnlisted(targetCode) {
  const code = String(targetCode || "").trim().toLowerCase();
  if (PUBLIC_COURSE_TARGETS.has(code)) return false;
  return isCourseTarget(code) && !showNewTargetLangs();
}
